import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ConfigProvider } from 'antd';
import { ColumnGroupType, ColumnsType, ColumnType } from 'antd/lib/table';
import { IPOButton, IPOAccount } from '@hylaa-npm/ipod';

import { PUploadFn } from '@/components/PUpload';
import PageContainer, { IPageRef } from '@/components/Page';
import PAvatarName from '@/components/PAvatarName';
import { PSearchTool, SearchEnum } from '@/components/PSearchTool';
import Empty from '@/components/Empty';

import {
	addStaff,
	changeStaffBasic,
	changeStaffRole,
	changeStaffStatus,
	getStaff,
	getStaffs,
	resetStaffPwd,
} from '@/apis/account';

import { CustomTable } from '@/styles/components/CustomBlueTable';
import { ContentTitleWrap } from './style';

const StaffEnabled: FC<{}> = () => {
	const { t } = useTranslation();
	const pageRef = useRef<IPageRef>();
	const currentUser = useSelector((state: { userInfo: IPOAccount.IStaff }) => state.userInfo);

	const [staffs, setStaffs] = useState<IPOAccount.IStaff[]>([]);
	const [staff, setStaff] = useState<IPOAccount.IStaff>();
	const [avatar, setAvatar] = useState<string>('');

	const [isStaffLoading, setIsStaffLoading] = useState<boolean>(true);
	const [isShowAddStaff, setIsShowAddStaff] = useState<boolean>(false);

	const [total, setTotal] = useState<number>(0);
	const [pagination, setPagination] = useState<IPagination>({ take: 0, offset: 0 });
	const [name, setName] = useState<string>('');

	useEffect(() => {
		if (pageRef.current) {
			const take = pageRef.current?.pageSize;
			setPagination({ take, offset: 0 });
		}
	}, [pageRef]);

	useEffect(() => {
		getStaffList(name, pagination);
	}, [JSON.stringify(name), JSON.stringify(pagination)]);

	const StaffColumns: ColumnsType<IPOAccount.IStaff> = [
		{
			title: t('staff.name'),
			key: 'name',
			ellipsis: true,
			render: (text: unknown, info: IPOAccount.IStaff) => (
				<PAvatarName
					id={info.id}
					phoneNum={info.mobile}
					name_EN={info.name}
					name_ZH={info.name2}
					avatar={info.avatar}
				/>
			),
		},
		{
			title: t('staff.phone'),
			key: 'name',
			ellipsis: true,
			render: (text: unknown, info: IPOAccount.IStaff) => <span>{info.mobile}</span>,
		},
		{
			title: t('staff.email'),
			key: 'name',
			ellipsis: true,
			render: (text: unknown, info: IPOAccount.IStaff) => <span>{info.email || '-'}</span>,
		},
		{
			title: t('staff.status'),
			key: 'desc',
			ellipsis: true,
			render: (text: unknown, info: IPOAccount.IStaff) => <span>{t('staff.active')}</span>,
		},
	];

	const getStaffList: (param: string, pagination: IPagination) => void = (param, pagination) => {
		if (pagination.take > 0) {
			getStaffs(1, param, pagination)
				.then((res: unknown) => {
					const data = res as { list: IPOAccount.IStaff[]; pagination: IPagination };

					setStaffs(data.list);
					setTotal(data.pagination.size ? data.pagination.size : data.list.length);
					setIsStaffLoading(false);
				})
				.catch((rej: unknown) => {
					setIsStaffLoading(false);
				});
		}
	};

	const handleAddStaff: (staff: IPOAccount.IAddStaff) => Promise<void> = (staff) => {
		return new Promise((res) => {
			addStaff({ ...staff, avatar }).then((res) => {
				setStaffs([res, ...staffs]);
				setAvatar('');
			});
			setIsShowAddStaff(false);
			res();
		});
	};

	const handleUpdateStaffRole: (role: IPOAccount.StaffRoleEnum) => Promise<void> = (role) => {
		return new Promise<void>((res) => {
			changeStaffRole(staff?.id ?? '', role).then(() => res());
		});
	};

	const handleUpdateStaffBasic: (basic: IPOAccount.IEditAccount) => Promise<void> = (basic) => {
		return new Promise<void>((resolve) => {
			changeStaffBasic(staff?.id ?? '', basic).then((res) => {
				let copyStaffs: IPOAccount.IStaff[] = [...staffs];
				copyStaffs.forEach((cStaff: IPOAccount.IStaff) => {
					if (cStaff.id === staff?.id) {
						cStaff.avatar = res.avatar;
						cStaff.email = res.email;
						cStaff.name = res.name;
						cStaff.name2 = res.name2;
					}
				});
				setStaffs([...copyStaffs]);
				resolve();
			});
		});
	};

	const handleDiasbledStaff: () => Promise<void> = () => {
		return new Promise((res) => {
			changeStaffStatus(staff?.id ?? '', 'disable').then(() => {
				setStaffs(staffs.filter((item) => item.id !== staff?.id));
				res();
			});
		});
	};

	const handleResetStaffPwd: (pwd: string) => Promise<void> = (pwd) => {
		return new Promise<void>((resolve) => {
			resetStaffPwd(staff?.id ?? '', pwd).then(() => resolve());
		});
	};

	const getSearchEle: () => ReactNode = () => {
		return (
			<PSearchTool
				tools={[{ key: 'name', placeholder: t('common.plInput'), type: SearchEnum.INPUT }]}
				handleSearch={(value) => {
					setName(value.name);
					setPagination({ ...pagination, offset: 0 });
				}}
			/>
		);
	};

	const getContentTitleEle: () => ReactNode = () => {
		return (
			<ContentTitleWrap>
				<span>{t('staff.staffList')}</span>
				<IPOButton onClick={() => setIsShowAddStaff(true)}>{t('staff.addStaff')}</IPOButton>
			</ContentTitleWrap>
		);
	};

	const getContentEle: () => ReactNode = () => {
		const getEmptyType: () => 'search' | 'nodata' = () => {
			if (name) {
				return 'search';
			} else {
				return 'nodata';
			}
		};
		return (
			<ConfigProvider
				renderEmpty={() => (
					<Empty type={getEmptyType()} tips={getEmptyType() === 'nodata' ? '暂无数据' : ''} />
				)}>
				<CustomTable
					rowKey="id"
					dataSource={staffs}
					columns={StaffColumns as (ColumnGroupType<object> | ColumnType<object>)[]}
					pagination={false}
					loading={isStaffLoading}
					onRow={(record) => {
						return {
							onClick: (event) =>
								getStaff(((record as unknown) as IPOAccount.IStaff).id).then((res) =>
									setStaff(res),
								),
						};
					}}
				/>
			</ConfigProvider>
		);
	};

	return (
		<>
			<PageContainer
				pageRef={pageRef}
				pageTitle={t('staff.staff')}
				searchEle={getSearchEle()}
				contentTitle={getContentTitleEle()}
				contentEle={getContentEle()}
				pagi={{
					total,
					handlePageChange: (offset) => setPagination({ ...pagination, ...{ offset } }),
				}}
			/>
			<IPOAccount.AddStaff
				showRight
				uploadEle={<PUploadFn.User avatar={avatar} changeAvatar={(id) => setAvatar(id)} />}
				isShowAdd={isShowAddStaff}
				AddUserReq={(staff) => handleAddStaff(staff)}
				handleChange={() => {
					setIsShowAddStaff(false);
					setAvatar('');
				}}
			/>
			{staff && (
				<IPOAccount.EditStaff
					uploadEle={
						<PUploadFn.User
							avatar={staff.avatar}
							changeAvatar={(id) => setStaff({ ...staff, avatar: id })}
						/>
					}
					user={staff}
					isShowEdit={!!staff}
					showRight
					isDisabledRight={currentUser.id === staff.id}
					handleChangeStatus={() => {
						setStaff(undefined);
					}}
					handleUpdatePwd={(pwd) => handleResetStaffPwd(pwd)}
					handleDisabled={() => handleDiasbledStaff()}
					handleUpdateRole={(role) => handleUpdateStaffRole(role)}
					handleUpdateBasic={(basic) => handleUpdateStaffBasic(basic)}
				/>
			)}
		</>
	);
};

export default StaffEnabled;
